import React, { useState, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Tooltip
} from 'reactstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import Cropper from 'react-easy-crop';
import ApiClient from '../BaseUrl/ApiClient';

import "../../../src/Components/Common/crop.css";
import { getDLDealCroppedImg } from "../../../src/Components/Common/DealCropDL"

const mapContainerStyle = { height: '400px', width: '100%' };
const DEFAULT_COORDINATES = { lat: 43.6511, lng: -79.3470 };

function AddBusinessProfile() {
  const history = useHistory();
  const [googleTooltipOpen, setGoogleTooltipOpen] = useState(false);
  const toggleGoogleTooltip = () => setGoogleTooltipOpen(!googleTooltipOpen);

const [formData, setFormData] = useState({
  businessName: '',
  telegramLink: '',
  googleBusinessLink: '',
  websiteLink: '',
  lunchSpecialStartTime: '',
  lunchSpecialEndTime: '',
  dailySpecialStartTime: '',
  dailySpecialEndTime: '',
  longitude: null,
  latitude: null,
  type: 'Point',
  postalCode: '',
  city: '',
  state: '',
  streetAddress: '',
  location: '',
  removeLogo: false
});


  const [selectedPosition, setSelectedPosition] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_COORDINATES);

  // Cropper states
  const fileInputRef = useRef(null);
  const [logoPhotoPreview, setLogoPhotoPreview] = useState(null);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [selectedFileName, setSelectedFileName] = useState("");

  const [logoPhoto, setLogoPhoto] = useState(null);

  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  });

  // -------------------- Google Maps Handlers --------------------
  const handleMapClick = useCallback((event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setSelectedPosition({ lat, lng });
    setMapCenter({ lat, lng });
    updateAddressFromCoordinates(lat, lng);
  }, []);

  const handleMarkerDragEnd = (event) => handleMapClick(event);

  const extractStreetAddress = (formattedAddress, components) => {
    const get = (type, field) => components.find(c => c.types.includes(type))?.[field] || '';
    const removeParts = [
      get('locality', 'long_name'),
      get('locality', 'short_name'),
      get('administrative_area_level_2', 'long_name'),
      get('administrative_area_level_2', 'short_name'),
      get('administrative_area_level_1', 'long_name'),
      get('administrative_area_level_1', 'short_name'),
      get('postal_code', 'long_name'),
      get('country', 'long_name'),
      get('country', 'short_name')
    ].filter(Boolean);

    return formattedAddress
      .split(',')
      .map(part => part.trim())
      .filter(part => {
        let cleaned = part;
        removeParts.forEach(rp => {
          cleaned = cleaned.replace(new RegExp(rp.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), '').trim();
        });
        return cleaned.length > 0;
      })
      .join(', ');
  };

  const updateAddressFromCoordinates = (lat, lng) => {
    setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const components = results[0].address_components;
        const getComp = type => components.find(c => c.types.includes(type))?.long_name || '';
        setFormData(prev => ({
          ...prev,
          postalCode: getComp('postal_code'),
          city: getComp('locality') || getComp('administrative_area_level_2'),
          state: getComp('administrative_area_level_1'),
          streetAddress: extractStreetAddress(results[0].formatted_address, components),
          location: results[0].formatted_address
        }));
      }
    });
  };

  const handleLocationBlur = () => {
    if (!formData.location) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: formData.location }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        const lat = loc.lat();
        const lng = loc.lng();
        setSelectedPosition({ lat, lng });
        setMapCenter({ lat, lng });
        updateAddressFromCoordinates(lat, lng);
      } else {
        // toast.error('Location not found on map');
      }
    });
  };

  // Geocode location and return coordinates as a promise
  const geocodeLocation = (address) => {
    return new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const loc = results[0].geometry.location;
          const lat = loc.lat();
          const lng = loc.lng();
          const components = results[0].address_components;
          const getComp = type => components.find(c => c.types.includes(type))?.long_name || '';
          resolve({
            latitude: lat,
            longitude: lng,
            postalCode: getComp('postal_code'),
            city: getComp('locality') || getComp('administrative_area_level_2'),
            state: getComp('administrative_area_level_1'),
            streetAddress: extractStreetAddress(results[0].formatted_address, components),
            location: results[0].formatted_address
          });
        } else {
          reject(new Error('Location not found'));
        }
      });
    });
  };

  // -------------------- Input Handlers --------------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // -------------------- Cropper Handlers --------------------
  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImageToCrop(reader.result);
      setShowCropper(true);
      e.target.value = "";
    };
    reader.readAsDataURL(file);
  };

  const handleCropSave = async () => {
    try {
      const blob = await getDLDealCroppedImg(imageToCrop, croppedAreaPixels);
      const file = new File([blob], "logo.jpg", { type: "image/jpeg" });
      setFormData(prev => ({ ...prev, logoPhoto: file }));
      setLogoPhotoPreview(URL.createObjectURL(blob));
      setShowCropper(false);
    } catch {
      toast.error("Image crop failed");
    }
  };

  // -------------------- Form Submission --------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    // if (!formData.businessName) {
    //   toast.error('Business Name is required');
    //   return;
    // }
    // if (!formData.latitude || !formData.longitude) {
    //   toast.error('Business Location is required');
    //   return;
    // }

    try {
      let submitData = { ...formData };

      // If location is provided but lat/lng are missing, geocode the location first
      if (submitData.location && (!submitData.latitude || !submitData.longitude)) {
        try {
          const geoData = await geocodeLocation(submitData.location);
          submitData = { ...submitData, ...geoData };
          // Also update the form state and map
          setFormData(prev => ({ ...prev, ...geoData }));
          setSelectedPosition({ lat: geoData.latitude, lng: geoData.longitude });
          setMapCenter({ lat: geoData.latitude, lng: geoData.longitude });
        } catch (geoErr) {
          toast.error('Could not find coordinates for the entered location. Please select from map or enter a valid address.');
          return;
        }
      }

      const data = new FormData();
      Object.entries(submitData).forEach(([key, value]) => {
        if (value && key !== 'logoPhoto') data.append(key, value);
      });

      if (submitData.logoPhoto) data.append('logoPhoto', submitData.logoPhoto);

      await ApiClient().post('/business/admin/create', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Business profile created successfully');
      history.push('/business-list');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.responseMessage?.[0] || 'Failed to create business profile');
    }
  };

  if (!isLoaded) return <div>Loading Google Maps...</div>;

  return (
    <Container className="mt-4">
      <ToastContainer />
      <h3>Add New Business Profile</h3>
      <Form onSubmit={handleSubmit} className="mb-4" encType="multipart/form-data">

        {/* Row 1: Business Name & Logo */}
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label>Business Name *</Label>
              <Input
                type="text"
                name="businessName"
                value={formData.businessName}
                onChange={handleInputChange}
                required
              />
            </FormGroup>
          </Col>

         <Col md={6}>
  <FormGroup className="d-flex flex-column align-items-start">
    <Label>Logo Photo</Label>
    <div className="d-flex align-items-center gap-2">
      {/* Display file name */}
      <Input
        type="text"
        value={selectedFileName || ""}
        placeholder="No file chosen"
        readOnly
        style={{ width: '500px' }}
      />
      <Button
        type="button"
        className="w-50"
        color="primary"
        onClick={() => fileInputRef.current.click()}
      >
        Choose File
      </Button>
    </div>

    <input
      ref={fileInputRef}
      type="file"
      accept="image/*"
      onChange={(e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setSelectedFileName(file.name); 
        const reader = new FileReader();
        reader.onload = () => {
          setImageToCrop(reader.result);
          setShowCropper(true);
          e.target.value = "";
        };
        reader.readAsDataURL(file);
      }}
      hidden
    />

    {logoPhotoPreview && (
      <div style={{ position: 'relative', display: 'inline-block', marginTop: 8 }}>
        {/* Image Preview */}
        <img
          src={logoPhotoPreview}
          alt="Logo Preview"
          className="rounded border"
          width="100"
        />

        {/* Cross Icon */}
        <span
          onClick={() => {
            setLogoPhoto(null);
            setImageToCrop(null);
            setLogoPhotoPreview(null);
            setSelectedFileName("");
            setFormData(prev => ({ ...prev, logoPhoto: null, removeLogo: true }));
          }}
          style={{
            position: 'absolute',
            top: -5,
            right: -5,
            background: '#ff3c3fff',
            color: 'white',
            borderRadius: '50%',
            width: 20,
            height: 20,
            textAlign: 'center',
            lineHeight: '20px',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: 14
          }}
        >
          ×
        </span>
      </div>
    )}
  </FormGroup>
</Col>


        </Row>

        {/* Row 2: Location & Google Map */}
        <Row>
          <Col md={12}>
            <FormGroup>
              <Label>Location *</Label>
              <Input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                onBlur={handleLocationBlur}
                placeholder="Enter address or click on map"
              />
            </FormGroup>
          </Col>

          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={13}
            onClick={handleMapClick}
            className="my-3 mx-3"
          >
            {selectedPosition && (
              <Marker
                position={selectedPosition}
                draggable={true}
                onDragEnd={handleMarkerDragEnd}
              />
            )}
          </GoogleMap>

          <div className="mt-2">
            <span>Latitude: {formData.latitude || 'Not set'}</span>
            <span className="ms-3">Longitude: {formData.longitude || 'Not set'}</span>
          </div>
        </Row>

        {/* Row 3: Other Details */}
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label>
                Google Link
                <span
                  id="googleBusinessInfo"
                  style={{ cursor: 'pointer', marginLeft: '6px', color: '#0d6efd' }}
                >
                  ⓘ
                </span>
              </Label>
              <Input
                type="text"
                name="googleBusinessLink"
                value={formData.googleBusinessLink}
                onChange={handleInputChange}
              />
              <Tooltip
                placement="right"
                isOpen={googleTooltipOpen}
                target="googleBusinessInfo"
                toggle={toggleGoogleTooltip}
              >
                Click for hours, phone number, or more info
              </Tooltip>
            </FormGroup>
          </Col>

          {/* <Col md={6}>
            <FormGroup>
              <Label>Telegram Link</Label>
              <Input
                type="text"
                name="telegramLink"
                value={formData.telegramLink}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col> */}
          {/* <Col md={6}>
            <FormGroup>
              <Label>Lunch Special Time</Label>
              <Input
                type="time"
                name="lunchSpecialTime"
                value={formData.lunchSpecialTime}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col> */}
          <Col md={6}>
            <FormGroup>
              <Label>Website Link</Label>
              <Input
                type="text"
                name="websiteLink"
                value={formData.websiteLink}
                onChange={handleInputChange}
                placeholder="https://example.com"
              />
            </FormGroup>
          </Col>

          <Col md={6}>
  <FormGroup>
    <Label>Lunch Special Time</Label>
    <div className="d-flex gap-2">
      <Input
        type="time"
        name="lunchSpecialStartTime"
        value={formData.lunchSpecialStartTime || ''}
        onChange={handleInputChange}
      />
      <span className="align-self-center">to</span>
      <Input
        type="time"
        name="lunchSpecialEndTime"
        value={formData.lunchSpecialEndTime || ''}
        onChange={handleInputChange}
      />
    </div>
  </FormGroup>
</Col>

        </Row>

        {/* <Row>
         
          <Col md={6}>
            <FormGroup>
              <Label>Daily Special Time</Label>
              <Input
                type="time"
                name="dailySpecialTime"
                value={formData.dailySpecialTime}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row> */}

        <Button color="primary" type="submit">Add Business Profile</Button>
      </Form>

      {/* Cropper Modal */}
      {showCropper && (
        <div className="crop-modal">
          <div className="crop-modal-content">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4>Crop Logo</h4>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => setShowCropper(false)}
              >
                &times;
              </button>
            </div>

            <div className="crop-container">
              <Cropper
                image={imageToCrop}
                crop={crop}
                zoom={zoom}
                aspect={1} // Square logo
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </div>

            <div className="crop-controls">
              <label>Zoom</label>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
            </div>

            <div className="crop-buttons">
              <button className="btn btn-secondary" onClick={() => setShowCropper(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCropSave}>Save Crop</button>
            </div>
          </div>
        </div>
      )}

    </Container>
  );
}

export default AddBusinessProfile;
